'use client';

import { Text, Stack, Box, Emoji, Button, LinkNext, Flex } from '@instamenu/ui-system-next';
import { themeVars } from '@instamenu/ui-vanilla-extract';
import { DeliveryStatus, OrderStatus, Order } from '@instamenu/data-access';
import { deliveryStatusMap, getCustomerFacingDeliveryStatus } from './status-map';
import * as styles from './delivery-tracker.css';
import { DeliveryStep } from './delivery-step';
import { Order as OrderFromCheckout } from '@instamenu/context-checkout';
import { observer } from 'mobx-react-lite';
import { formatPhone } from '@instamenu/formatters';
import { Phone, User } from 'lucide-react';

export type DeliveryTrackerProps = {
  order: OrderFromCheckout | Order;
  hasDeliveryInfo?: boolean;
};

const DELIVERY_STEPS = [
  OrderStatus.Placed,
  DeliveryStatus.Submitted,
  DeliveryStatus.PickupEnroute,
  DeliveryStatus.AtPickup,
  DeliveryStatus.DropoffEnroute,
  DeliveryStatus.Delivered,
];

export const getDeliveryError = (order: DeliveryTrackerProps['order']): boolean => {
  const orderStatus = deliveryStatusMap(order.status);
  const customerFacingDeliveryStatus = getCustomerFacingDeliveryStatus(order.delivery?.status);
  const deliveryStatus = customerFacingDeliveryStatus
    ? deliveryStatusMap(order.status, customerFacingDeliveryStatus)
    : null;

  return orderStatus.isError || (deliveryStatus?.isError ?? false);
};

export const DeliveryTracker = observer(({ order }: DeliveryTrackerProps) => {
  const currentDeliveryStep = getCustomerFacingDeliveryStatus(order.delivery?.status);
  const currentStepIndex = DELIVERY_STEPS.indexOf(currentDeliveryStep ?? OrderStatus.Placed);
  const hasError = getDeliveryError(order);
  const isCompleted = order.delivery?.status === DeliveryStatus.Delivered;

  if (hasError) {
    return (
      <Stack className={styles.errorContainer}>
        <Text variant="h2">
          <Emoji emoji="🤕" ariaLabel="oh-oh" />
        </Text>
        <Text variant="body1">
          {deliveryStatusMap(order.status, currentDeliveryStep).description}
        </Text>
      </Stack>
    );
  }

  return (
    <Box>
      <Text variant="h2" css={{ textAlign: 'left' }}>
        {isCompleted ? 'Order Delivered' : 'Delivery Status'}
      </Text>

      <Stack className={styles.trackerContainer}>
        {/* Vertical line — left value keeps the line centered behind the 32px
            indicators (32/2 - lineWidth/2 ≈ 14.5px). */}
        <Box
          className={styles.deliverySteps}
          data-progress={currentStepIndex}
          css={{ left: '15px' }}
        />
        {DELIVERY_STEPS.map((step, index) => (
          <DeliveryStep
            key={step}
            step={step}
            isActive={index === currentStepIndex}
            isCompleted={index < currentStepIndex}
            delivery={order.delivery}
          />
        ))}
      </Stack>

      {order.delivery?.driverName && (
        <>
          <Box className={styles.divider} />
          <Stack css={{ gap: themeVars.pad[16] }}>
            <Text variant="h3" css={{ textAlign: 'left' }}>
              Your Driver
            </Text>
            <Box className={styles.driverInfo}>
              <Flex css={{ gap: themeVars.pad[12], flex: 1, alignItems: 'center' }}>
                <Box className={styles.driverAvatar}>
                  <User size={24} color={themeVars.color.brand} />
                </Box>
                <Text variant="caption2" css={{ fontWeight: 500 }}>
                  {order.delivery.driverName}
                </Text>
              </Flex>

              {order.delivery.driverPhone && (
                <LinkNext href={`tel:${order.delivery.driverPhone}`} className={styles.phoneButton}>
                  <Phone size={16} />
                  <Text variant="caption2" css={{ fontWeight: 500 }}>
                    {formatPhone(order.delivery.driverPhone)}
                  </Text>
                </LinkNext>
              )}
            </Box>
          </Stack>
        </>
      )}

      {order.delivery?.trackingUrl && (
        <>
          <Box className={styles.divider} />
          <Button
            as="a"
            href={order.delivery.trackingUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Track Order
          </Button>
        </>
      )}
    </Box>
  );
});
